import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ContentCopyRounded as CopyIcon,
  SummarizeRounded as SummaryIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import AskRecordAnswerText from './AskRecordAnswerText';
import AskRecordSourceList from './AskRecordSourceList';
import { ASK_ACCENT, ASK_ACCENT_DARK, ASK_COLORS } from './askRecordUi';

const copyText = async (text) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    throw new Error('Clipboard is unavailable.');
  }
  await navigator.clipboard.writeText(String(text || ''));
};

export const AskRecordCopyAction = ({ label, text }) => {
  const [status, setStatus] = useState('idle');
  const handleCopy = async () => {
    try {
      await copyText(text);
      setStatus('copied');
    } catch (_error) {
      setStatus('failed');
    }
  };
  const visibleLabel = status === 'copied'
    ? 'Copied'
    : status === 'failed' ? 'Copy failed' : label;

  return (
    <ButtonBase
      onClick={handleCopy}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.45,
        mt: 0.25,
        px: 0.8,
        py: 0.45,
        borderRadius: '999px',
        color: ASK_ACCENT_DARK,
        bgcolor: alpha(ASK_ACCENT, 0.08),
      }}
    >
      <CopyIcon sx={{ fontSize: 15 }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>
        {visibleLabel}
      </Typography>
    </ButtonBase>
  );
};

export const AskRecordSummarySection = ({
  createSummary,
  loading,
  onOpenSource,
  summary,
  summaryError,
}) => (
  <Box
    sx={{
      mt: 1.4,
      p: 1.1,
      borderRadius: '16px',
      bgcolor: ASK_COLORS.surface,
      border: `1px solid ${alpha(ASK_ACCENT, 0.14)}`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ color: colors.semantic.ink, fontSize: '0.86rem', fontWeight: 900 }}>
        Conversation summary
      </Typography>
      {!summary ? (
        <ButtonBase
          onClick={createSummary}
          disabled={loading}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.55,
            borderRadius: '999px',
            bgcolor: alpha(ASK_ACCENT, 0.12),
            color: ASK_ACCENT_DARK,
          }}
        >
          {loading ? <CircularProgress size={14} color="inherit" /> : <SummaryIcon sx={{ fontSize: 16 }} />}
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 850 }}>
            {loading ? 'Creating…' : 'Create summary'}
          </Typography>
        </ButtonBase>
      ) : null}
    </Box>

    {summaryError ? (
      <Typography sx={{ mt: 0.8, color: colors.semantic.error, fontSize: '0.76rem' }}>
        {summaryError}
      </Typography>
    ) : null}

    {summary ? (
      <Box sx={{ mt: 1 }}>
        <AskRecordAnswerText answer={summary.summary} sources={summary.sources || []} />
        <AskRecordSourceList onOpenSource={onOpenSource} sources={summary.sources || []} />
        <AskRecordCopyAction label="Copy summary" text={summary.summary} />
      </Box>
    ) : null}
  </Box>
);

export default AskRecordSummarySection;
